import { Inject, Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { UserdeviceService } from '../services/userdevice.service';
import { ENV, IEnvironment } from '@cleverly/shared/core';
import { initializeApp } from 'firebase/app';
import { AuthService } from '@cleverly/shared/api-authorization';
import { FirebaseMessaging, PermissionStatus } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { PushNotificationSchema } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import { NotificationsService } from '@cleverly/shared/common/services/notifications.service';
import { PushNotificationsVMs } from '@cleverly/shared/data/models/viewmodels.PushNotificationsVMs';

@Injectable({
    providedIn: 'root',
})
export class PushmanagerService {
    private unsubscribeReciever: Subject<void> = new Subject();
    sessionSubscription: Subscription;
    private receivedMessages: string[] = [];

    constructor(
        private userdeviceService: UserdeviceService,
        private authService: AuthService,
        private notificationService: NotificationsService,
        @Inject(ENV) private env: IEnvironment
    ) {}

    initPushservice() {
        // Initialize Firebase
        initializeApp(this.env.firebaseConfig);
        this.requestPermission();

        this.addNotificationReceivedListener();
    }

    protected requestPermission(): Promise<void | PermissionStatus> {
        return FirebaseMessaging.requestPermissions()
            .then(async (permission) => {
                if (permission.receive === 'granted') {
                    const token = await this.getToken();
                    this.storeUserDeviceToken(token.token);
                } else {
                    console.log('Unable to get permission to notify.');
                }

                return permission;
            })
            .catch((e) => {
                console.error('Error requesting permission:', e);
            });
    }

    protected storeUserDeviceToken(token: string) {
        const subscription = this.authService.accessToken$.subscribe((accessToken) => {
            if (accessToken) {
                this.userdeviceService.SaveUserDevice(token).subscribe(() => {
                    subscription.unsubscribe();
                });
            }
        });
    }

    protected async getToken() {
        const token = await FirebaseMessaging.getToken({ vapidKey: this.env.firebaseWebpushPublic });
        return token;
    }

    protected async addNotificationReceivedListener() {
        await FirebaseMessaging.addListener('notificationReceived', async (event: any) => {
            if (this.receivedMessages.includes(event.notification.id)) {
                return;
            }

            const state = await App.getState();

            if (state.isActive) {
                const messageType = event.notification.data?.messageType ?? 'cleverlyChat';

                const translations = this.translateMessageType(messageType);

                this.notificationService.addMessageToQueue({
                    severity: 'info',
                    summary: translations.summary,
                    detail: translations.detail,
                    data: event.notification.data,
                });
            }
        });

        await FirebaseMessaging.addListener('notificationActionPerformed', (event: any) => {
            if (Capacitor.getPlatform() !== 'web' && event.actionId === 'tap') {
                if (window.location.href !== event.notification?.data?.link) {
                    const baseUrl = `${location.protocol}//${location.host}`;
                    const url = new URL(baseUrl + event.notification.data.link);
                    const slug = url.pathname + url.search;

                    location.assign(slug);
                }
            }
        });
    }

    protected async removeAllListeners() {
        await FirebaseMessaging.removeAllListeners();
    }

    protected getMessageType(notification: PushNotificationSchema) {
        let messageType = notification.data?.messageType ?? 'cleverlyChat';

        if (messageType.length == 0) {
            messageType = 'cleverlyChat';
        }
        return messageType;
    }

    protected translateMessageType(messageType: string) {
        const matchRequestAssigned = PushNotificationsVMs.MatchRequestPushEnum.MatchRequestAssigned;
        const matchRequestAssignedName = PushNotificationsVMs.MatchRequestPushEnum[matchRequestAssigned];

        if (messageType === matchRequestAssignedName) {
            return {
                summary: $localize`Match request assigned`,
                detail: $localize`You have been assigned a new match request`,
            };
        }

        return {
            summary: '',
            detail: '',
        };
    }
}
